import type { DefaultError } from '@tanstack/react-query'
import { ApiError } from '../api-client/api-error'
import { ZodParseError } from '../api-client/api-hooks'
import { ConstraintViolationProblemSchema } from '../api-client/api-schemas'

export const getErrorMessage = (error: DefaultError) => {
  if (error instanceof ZodParseError) {
    return 'Something went wrong processing this request.'
  }

  if (error instanceof ApiError) {
    const { problemDetail: detail, problemTitle: title } = error
    if (detail) {
      return detail
    }

    if (title) {
      return title
    }
  }

  return 'An unexpected error occurred. Please try again.'
}

/**
 * The violations of a constraint-violation problem, joined: that problem's detail only counts them.
 *
 * Opt-in through `useShowMutationError`'s `getMessage` rather than part of `getErrorMessage`: most
 * constraints answer with a bare field-level fragment ("must not be blank") that reads as nonsense
 * without its field, so only a caller whose constraints are written as sentences should show them.
 * A body that is not a constraint violation, or lists none, gives undefined so the caller falls back
 * to the generic message.
 */
export const getConstraintViolationMessage = (error: DefaultError): string | undefined => {
  if (!(error instanceof ApiError)) {
    return undefined
  }
  const violations = ConstraintViolationProblemSchema.safeParse(error.problemBody).data?.violations ?? []
  return violations.length ? violations.map(violation => violation.message).join(' ') : undefined
}

/**
 * The message to show under a picker whose list could not be fetched: the first failure among the
 * queries behind it, in the order they were issued, or undefined when none failed.
 *
 * A failed lookup leaves the list empty, which the user reads as "this connection can see nothing" —
 * the same thing an empty account looks like. `getErrorMessage` is the text the mutation toasts
 * already use, so a problem detail from the API is shown verbatim and anything else falls back to
 * the standard sentence.
 */
export const getPickerErrorMessage = (...errors: (DefaultError | null)[]) => {
  const failure = errors.find(error => error !== null)
  return failure ? getErrorMessage(failure) : undefined
}

export const getErrorTitle = (error: DefaultError, prefix?: string) => {
  const errorTitle = error instanceof ApiError && error.problemTitle ? error.problemTitle : 'Unexpected issue'

  return prefix ? `${prefix}: ${errorTitle}` : errorTitle
}
